import { Select as BaseUiSelect, Option, SIZE, TYPE } from 'baseui/select'

import React, { useMemo } from 'react'
import { FixedSizeList as List } from 'react-window'

export type SelectProps = {
    value: Option | null
    placeholder?: string
    options: Option[]
    multi?: boolean
    onChange?: (value: Option | undefined | null) => void
    clearable?: boolean
    isLoading?: boolean
    disabled?: boolean
    disableSorting?: boolean
}

const SelectComponent = ({
    placeholder,
    value,
    options,
    multi = false,
    onChange,
    clearable = true,
    isLoading = false,
    disabled = false,
    disableSorting = false
}: SelectProps) => {
    const memoizedOptions = useMemo(() => options, [options])
    let height: number | null
    if (options) {
        height = options?.length > 10 ? 350 : options.length * 35
    }

    const VirtualizedMenu = React.forwardRef(({ children, ...props }: any, ref: React.Ref<any>) => (
        <List outerRef={ref} width="100%" height={height || 350} itemCount={children.length} itemSize={35} {...props}>
            {({ index, style }) => <div style={style}>{children[index]}</div>}
        </List>
    ))
    VirtualizedMenu.displayName = 'VirtualizedMenu'
    return (
        <BaseUiSelect
            disabled={disabled}
            overrides={{
                Dropdown: VirtualizedMenu,
                DropdownOption: {
                    style: {
                        width: '100%',
                        fontSize: '14px !important'
                    }
                },
                SingleValue: {
                    style: {
                        fontSize: '14px !important'
                    }
                },
                MultiValue: {
                    style: {
                        fontSize: '14px !important'
                    }
                },
                Placeholder: {
                    style: {
                        fontSize: '14px !important;'
                    }
                },
                ControlContainer: {
                    style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }
                }
            }}
            clearable={clearable}
            options={memoizedOptions}
            labelKey="label"
            valueKey="id"
            size={SIZE.compact}
            placeholder={placeholder || 'Choose'}
            maxDropdownHeight="300px"
            type={TYPE.search}
            onInputChange={(params) => {
                if (!params.target.value) {
                    onChange?.(null)
                }
            }}
            filterOptions={(options, filterValue) => {
                if (disableSorting) return options
                const filteredOptions = options.filter((option) => {
                    return (
                        typeof option?.label === 'string' &&
                        option.label.toLowerCase().includes(filterValue.toLowerCase())
                    )
                })

                const sortedOptions = filteredOptions.sort((a, b) => {
                    const aLength = typeof a?.label === 'string' ? a.label.length : 0 // Check if label is a string
                    const bLength = typeof b?.label === 'string' ? b.label.length : 0 // Check if label is a string

                    return aLength - bLength
                })

                return sortedOptions
            }}
            onChange={(onChangeParams) => onChange && onChange(onChangeParams.option)}
            multi={multi}
            isLoading={isLoading}
            escapeClearsValue={true}
            value={value ? [value] : []}
        />
    )
}

export const Select = React.memo(SelectComponent)
